import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Tag } from 'src/services/tag/Tag';
import { offlineTags } from './data/offlineTags';
import { TagService } from 'src/services/tag/tag.service';
import { isParent } from 'src/utils/isParent';
import { isDirectParent } from 'src/utils/isDirectParent';
import { archiveSelected } from 'src/utils/archiveSelected';

@Injectable({
  providedIn: 'root',
})
export class OfflineTagService implements TagService {
  MAX_LEVEL = 4;

  public tags = new BehaviorSubject<Tag[]>([]);

  public static readonly DEFAULT_COLOR = 'green';

  private localTags: Tag[] = [];

  constructor() {
    this.localTags = offlineTags;
    this.publish();
  }
  getNewId(): string {
    return Math.random().toString(36).substring(7);
  }

  async save(tags: Tag[]): Promise<void> {
    this.localTags = tags;
    this.publish();
  }

  async updateById(newTag: Tag) {
    const index = this.localTags.findIndex((t) => t.id === newTag.id);
    const oldTag = this.localTags[index];
    if (oldTag.fullPath === newTag.fullPath) {
      // Path has not changed, update the tag
      this.localTags[index] = newTag;
      this.publish();
      return;

      // Path has changed, update children path too
    } else {
      const pathParts = newTag.fullPath.split('/');

      if (pathParts.length > this.MAX_LEVEL) {
        throw new Error('Tag max deepness: ' + this.MAX_LEVEL);
      } else {
        const children = this.localTags.filter((t) =>
          isParent(oldTag.fullPath, t.fullPath)
        );

        children.forEach((child) => {
          const childPathParts = child.fullPath.split('/');
          const newPathParts = pathParts.concat(
            childPathParts.slice(oldTag.fullPath.split('/').length)
          );
          child.fullPath = newPathParts.join('/');
          const childIndex = this.localTags.findIndex((t) => t.id === child.id);
          this.localTags[childIndex] = child;
        });
        this.localTags[index] = newTag;
        this.publish();
        return;
      }
    }
  }

  async deleteById(id: string) {
    const tag = this.localTags.find((t) => t.id === id);
    if (tag) {
      const pathParts = tag.fullPath.split('/');
      this.localTags = this.localTags.filter((t) => {
        const tPathParts = t.fullPath.split('/');
        return !(
          tPathParts.length > pathParts.length &&
          tPathParts.slice(0, pathParts.length).join('/') === tag.fullPath
        );
      });
      this.localTags = this.localTags.filter((t) => t.id !== id);
      this.publish();
    }
  }

  async create(fullPath: string, isPeriodic?: boolean): Promise<string> {
    if (fullPath.split('/').length > this.MAX_LEVEL) {
      throw new Error('Tag max deepness: ' + this.MAX_LEVEL);
    }

    await this.createSubPathsIfNecessary(fullPath, isPeriodic);

    const id = this.getNewId();

    const level = fullPath.split('/').length;
    const location = fullPath.split('/').slice(0, -1).join('/');

    const tagsOnSameLevel = this.localTags.filter(
      (tag) =>
        tag.fullPath.split('/').length === level &&
        tag.fullPath.startsWith(location)
    );

    const newTag: Tag = {
      fullPath,
      color: OfflineTagService.DEFAULT_COLOR,
      isArchived: false,
      id,
      isPeriodic: isPeriodic,
    };

    this.localTags.push(newTag);
    this.publish();

    return id;
  }

  async createSubPathsIfNecessary(
    fullPath: string,
    isPeriodic?: boolean
  ): Promise<void> {
    const parts = fullPath.split('/');
    for (let i = 1; i < parts.length; i++) {
      const subPath = parts.slice(0, i).join('/');
      if (!this.localTags.find((tag) => tag.fullPath === subPath)) {
        await this.create(subPath, isPeriodic);
      }
    }
  }

  async archiveWithTree(tag: Tag, setToArchiveTrue = true): Promise<void> {
    const updatedTags = archiveSelected(tag, this.localTags, setToArchiveTrue);
    if (updatedTags) {
      this.localTags = updatedTags;
      this.publish();
      return Promise.resolve();
    } else {
      return Promise.reject();
    }
  }

  async archive(id: string) {
    const tag = this.localTags.find((t) => t.id === id);
    if (tag) {
      tag.isArchived = true;
      this.publish();
    }
  }

  async unarchive(id: string) {
    const tag = this.localTags.find((t) => t.id === id);
    if (tag) {
      tag.isArchived = false;
      this.publish();
    }
  }

  async duplicate(id: string, withChildren = true) {}

  publish() {
    this.tags.next(this.localTags);
  }
}
